/**
 * Códigos de ubicación: Z1-R03-N2-P05 (zona-rack-nivel-posición).
 * El código completo no se guarda: se arma al mostrar, para que renombrar un
 * rack actualice todos sus códigos. Niveles N1…N20 (N1 = el de abajo, junto al
 * piso) y posiciones P01…P99, igual que create_rack_with_grid
 * (supabase/migrations/20261013010000_create_rack_with_grid.sql).
 */

export const MAX_NIVELES = 20;
export const MAX_POSICIONES = 99;
export const NIVELES_BAJOS_POR_DEFECTO = 2;

export function codigoNivel(n: number): string {
  return `N${n}`;
}

export function codigoPosicion(n: number): string {
  return `P${String(n).padStart(2, '0')}`;
}

/** Une las partes que existan; si la zona no tiene código, el resultado empieza en el rack. */
export function codigoCompleto(partes: {
  zona?: string | null; rack?: string | null; nivel?: string | null; posicion?: string | null;
}): string {
  return [partes.zona, partes.rack, partes.nivel, partes.posicion]
    .map(p => (p ?? '').trim())
    .filter(Boolean)
    .join('-');
}

function siguienteConPrefijo(existentes: (string | null | undefined)[], prefijo: string): string {
  const re = new RegExp(`^${prefijo}(\\d+)$`, 'i');
  let max = 0;
  for (const c of existentes) {
    const m = (c ?? '').trim().match(re);
    if (m) max = Math.max(max, parseInt(m[1], 10));
  }
  return `${prefijo}${String(max + 1).padStart(2, '0')}`;
}

/** Siguiente código de rack libre: R01, R02… (ignora códigos con otro formato). */
export function sugerirSiguienteRack(codigosExistentes: (string | null | undefined)[]): string {
  return siguienteConPrefijo(codigosExistentes, 'R');
}

/** Siguiente código de zona libre dentro de una bodega: Z1, Z2… */
export function sugerirCodigoZona(codigosExistentes: (string | null | undefined)[]): string {
  const re = /^Z(\d+)$/i;
  let max = 0;
  for (const c of codigosExistentes) {
    const m = (c ?? '').trim().match(re);
    if (m) max = Math.max(max, parseInt(m[1], 10));
  }
  return `Z${max + 1}`;
}

export interface ValidacionRack { ok: boolean; error?: string }

/** Mismas reglas que create_rack_with_grid, para avisar antes de llamar a la base. */
export function validarRack(codigo: string, niveles: number, posiciones: number, nivelesBajos: number): ValidacionRack {
  if (!codigo.trim()) return { ok: false, error: 'El código del rack es obligatorio.' };
  if (codigo.trim().length > 20) return { ok: false, error: 'El código admite hasta 20 caracteres.' };
  if (!Number.isInteger(niveles) || niveles < 1 || niveles > MAX_NIVELES) return { ok: false, error: `Niveles: entre 1 y ${MAX_NIVELES}.` };
  if (!Number.isInteger(posiciones) || posiciones < 0 || posiciones > MAX_POSICIONES) return { ok: false, error: `Posiciones por nivel: entre 0 y ${MAX_POSICIONES}.` };
  if (!Number.isInteger(nivelesBajos) || nivelesBajos < 0 || nivelesBajos > niveles) return { ok: false, error: `Niveles bajos: entre 0 y ${niveles}.` };
  return { ok: true };
}

/** Vista previa de la generación masiva: primer y último código y total de ubicaciones. */
export function vistaPreviaRack(zona: string | null | undefined, rack: string, niveles: number, posiciones: number) {
  const r = rack.trim() || 'R??';
  if (posiciones > 0) {
    return {
      primero: codigoCompleto({ zona, rack: r, nivel: codigoNivel(1), posicion: codigoPosicion(1) }),
      ultimo: codigoCompleto({ zona, rack: r, nivel: codigoNivel(niveles), posicion: codigoPosicion(posiciones) }),
      total: niveles * posiciones,
      unidad: 'posiciones' as const,
    };
  }
  return {
    primero: codigoCompleto({ zona, rack: r, nivel: codigoNivel(1) }),
    ultimo: codigoCompleto({ zona, rack: r, nivel: codigoNivel(niveles) }),
    total: niveles,
    unidad: 'niveles' as const,
  };
}
